/* eslint-disable no-invalid-this */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import * as d3 from 'd3';
import _ from 'lodash';
import { ContentState, EditorState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import { AddIcon } from '../../../../components/Icon';
import { ReactComponent as Graphic } from '../../../../static/images/Graphic.svg';
import StyleButton from '../../../../components/StyleButton';
import { BS_TOOLBAR_STATUS } from '../../../../comm/constant';
import Cubicle from '../Cubicle';
import CreateCubicle from '../Popups/CreateCubicle';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import {
  addNonOverlappingElement,
  isOverlapping,
  initBedData,
  handleDeleteEmbedBug,
} from '../../../../utils/newBedStatusUtil';
import { bedStatusAPI } from '../../../../apis';
import { alertMsg } from '../../../../apis/commlogic';

const useStyles = makeStyles(() => ({
  wardName: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    color: '#2e4469',
  },
  graphic: {
    width: '9.375rem',
    height: '9.375rem',
  },
  addCubicle: {
    transform: 'translate(-6.25rem, -9rem)',
    overflow: 'auto',
  },
  addCubicleText: {
    fontFamily: 'NotoSans-Bold',
    color: '#707090',
    fontSize: '1rem',
  },
  floorRoot: ({ tool }) => ({
    cursor: tool.type === BS_TOOLBAR_STATUS.MOVE ? 'grab' : 'auto',
  }),
  gRoot: {},
}));

const FloorPlan = ({
  tool,
  setToolStatus,
  createPopOpen,
  handleCreatePopup,
  floorPlanData,
  update,
  setUpdate,
  setDragedBedList,
  cubicleList,
  setCubicleList,
  handleRemoveLinkedBed,
  handleUpdateLink,
  getCubicleListAPI,
}) => {
  const classes = useStyles({ tool });
  const [newCubicleKey, setNewCubicleKey] = useState(-10086);
  const svgRef = useRef();
  const gRef = useRef();
  const [d3Obj, setD3Obj] = useState({
    svg: null,
    g: null,
    cubicles: null,
  });
  const d3Zoom = d3.zoom();

  // update floor plan
  const handleSubmitFloorPlan = useCallback(async () => {
    try {
      let params = {
        cubicles: _.map(cubicleList, (item) => {
          return {
            bedNos: _.filter(item.data.beds, (bedItem) => bedItem.data).map((bedItem) => {
              return {
                bedColumn: bedItem.column,
                bedRow: bedItem.row,
                bedHead: bedItem.data.bedHead,
                linkedBedNo: bedItem.data.linkedBedNo,
                linkeds: _.map(bedItem.data.linkeds, (linkItem) => ({
                  bedNo: linkItem.bedNo,
                  ccWardCd: linkItem.ccWardCd,
                })),
              };
            }),
            cubColour: item.data.color,
            cubColums: String(item.data.bedArrangement.column),
            cubHeight: String(item.height),
            cubRows: String(item.data.bedArrangement.row),
            cubType: item.data.type,
            cubWidth: String(item.width),
            cubicleId: item.id > 0 ? item.id : null,
            header: item.data.header ?? 'show',
            gender: item.data.gender,
            name: item.data.name,
            position: JSON.stringify({
              X: item.x,
              Y: item.y,
            }),
            reColour: item.data.remarkColor,
            remark: item.data.type === 'Memo' ? handleDeleteEmbedBug(item.data.remarks) : item.data.remarks,
            status: item.data.status,
            borderLine: item.data.borderLine,
            version: item.data.version ?? null,
          };
        }),
        floorPlanId: floorPlanData.floorPlanId,
      };
      const { result } = await bedStatusAPI.updateCubicleBed(params);
      if (result === 'success') {
        getCubicleListAPI(() => alertMsg('Update successfully', 'Update Floor Plan Success', 'success', 3000));
      }
    } catch (error) {}
    setUpdate(false);
  }, [floorPlanData, cubicleList, getCubicleListAPI]);

  useEffect(() => {
    if (update) {
      handleSubmitFloorPlan();
    }
  }, [update, handleSubmitFloorPlan]);

  console.log('cubicleList', cubicleList);

  // 初始化 cubicle list
  useEffect(() => {
    if (floorPlanData) {
      const initCubicle = _.map(floorPlanData.cubicles, (item) => {
        const position = JSON.parse(item.position);
        let remarks = item.remark;

        if (item.cubType === 'Memo') {
          const blocksFromHtml = htmlToDraft(item.remark);
          const { contentBlocks, entityMap } = blocksFromHtml;
          const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
          remarks = EditorState.createWithContent(contentState);
        }

        return {
          x: position.X,
          y: position.Y,
          width: item.cubWidth,
          height: item.cubHeight,
          id: item.cubicleId,
          data: {
            name: item.name,
            type: item.cubType,
            status: item.status,
            gender: item.gender,
            color: item.cubColour,
            remarks: remarks,
            remarkColor: item.reColour,
            bedArrangement: { column: item.cubColums, row: item.cubRows },
            width: item.cubWidth,
            height: item.cubHeight,
            header: item.header ?? 'show',
            borderLine: item.borderLine,
            beds: initBedData(item.cubRows, item.cubColums, item.bedNos),
            version: item.version,
          },
        };
      });

      setNewCubicleKey((o) => o + 1);
      setCubicleList(initCubicle);

      // 把已经绑定到cubicle上面的beds记录下来
      setDragedBedList(() => {
        let tempList = [];
        _.forEach(floorPlanData.cubicles, (item) => {
          if (item.bedNos) {
            tempList = [...tempList, ...item.bedNos];
            _.forEach(item.bedNos, (bedItem) => {
              if (bedItem.linkeds) {
                tempList = [...tempList, ...bedItem.linkeds];
              }
            });
          }
        });
        return tempList;
      });
    }
  }, [floorPlanData]);

  // 处理拖拽事件的逻辑
  const dragged = useCallback(
    function (event) {
      let nonOverlapping = true;
      const dragEl = d3.select(this);
      d3Obj.g
        .selectAll('foreignObject')
        .data(cubicleList)
        .each(function () {
          const otherEl = d3.select(this);
          const a = {
            x: event.x,
            y: event.y,
            width: Number(dragEl.attr('width')),
            height: Number(dragEl.attr('height')),
            id: dragEl.attr('id'),
          };

          const b = {
            x: Number(otherEl.attr('x')),
            y: Number(otherEl.attr('y')),
            width: Number(otherEl.attr('width')),
            height: Number(otherEl.attr('height')),
            id: otherEl.attr('id'),
          };

          if (isOverlapping(a, b)) {
            nonOverlapping = false;
          }
        });

      if (nonOverlapping) {
        const newX = Math.max(0, event.x);
        const newY = Math.max(0, event.y);
        d3.select(this).attr('x', newX).attr('y', newY);
      }
    },
    [cubicleList]
  );

  // 拖拽结束，更新数据
  const dragended = useCallback(
    function () {
      if (d3Obj.cubicles) {
        let tempCubicles = [];
        d3Obj.cubicles.each(function () {
          const foreignObject = d3.select(this);
          tempCubicles.push({
            id: Number(foreignObject.attr('id')),
            x: Number(foreignObject.attr('x')),
            y: Number(foreignObject.attr('y')),
            width: Number(foreignObject.attr('width')),
            height: Number(foreignObject.attr('height')),
          });
        });

        setCubicleList((oldData) => {
          const result = _.map(oldData, (item) => {
            if (_.some(tempCubicles, (tempItem) => tempItem.id === item.id)) {
              const tempCubicle = _.find(tempCubicles, (tempItem) => tempItem.id === item.id);
              return {
                ...item,
                x: tempCubicle.x,
                y: tempCubicle.y,
                width: tempCubicle.width,
                height: tempCubicle.height,
              };
            } else {
              return item;
            }
          });

          return result;
        });
      }
    },
    [d3Obj]
  );

  // 添加拖拽事件
  useEffect(() => {
    if (tool.type === BS_TOOLBAR_STATUS.MOVE) {
      d3.select(gRef.current)
        .selectAll('foreignObject')
        .data(cubicleList)
        .call(d3.drag().on('drag', dragged).on('end', dragended));
    } else {
      d3.select(gRef.current)
        .selectAll('foreignObject')
        .data(cubicleList)
        .call(d3.drag().on('drag', null).on('end', null));
    }
  }, [dragged, dragended, cubicleList, tool]);

  const zoomed = useCallback(
    function ({ transform }) {
      const newX = Math.min(0, transform.x);
      const newY = Math.min(0, transform.y);
      const newTransform = d3.zoomIdentity.translate(newX, newY).scale(tool.zoom);
      d3Obj.g.attr('transform', newTransform);
    },
    [d3Obj.g, tool.zoom]
  );

  // 初始化 d3
  useEffect(() => {
    setD3Obj((o) => {
      o.svg = d3.select(svgRef.current);
      o.g = d3.select(gRef.current);
      o.cubicles = o.g.selectAll('foreignObject').data(cubicleList);
      return o;
    });
  }, [cubicleList]);

  // 添加 d3 zoom 事件
  useEffect(() => {
    d3Obj.svg.call(
      d3Zoom
        .filter((event) => {
          if (tool.type === BS_TOOLBAR_STATUS.MOVE) {
            return event.type === 'mousedown' || event.type === 'touchstart';
          } else {
            return false;
          }
        })
        .scaleExtent([0.5, 2])
        .on('zoom', zoomed)
    );
  }, [d3Obj.svg, zoomed, tool.type]);

  // zoom-in, zoom-out
  useEffect(() => {
    const transform = d3.zoomTransform(gRef.current);
    const newX = Math.min(0, transform.x);
    const newY = Math.min(0, transform.y);
    const newTransform = d3.zoomIdentity.translate(newX, newY).scale(tool.zoom);
    d3Obj.g.attr('transform', newTransform);
  }, [tool.zoom, d3Obj.svg]);

  // reset
  useEffect(() => {
    d3Obj.svg.call(d3Zoom.transform, d3.zoomIdentity);
    const resetTransform = d3.zoomIdentity.translate(0, 0).scale(1);
    d3Obj.g.attr('transform', resetTransform);
  }, [tool.reset, d3Obj]);

  // 添加 cubicle
  const handleCreateCubicle = useCallback(
    ({ data, width, height }) => {
      setCubicleList((oldData) => {
        const newCubicle = {
          x: -10000,
          y: 0,
          width,
          height,
          id: newCubicleKey,
          data,
        };

        setNewCubicleKey((o) => o + 1);
        handleCreatePopup(false)();
        return [...oldData, newCubicle];
      });
      setToolStatus((o) => ({
        ...o,
        type: BS_TOOLBAR_STATUS.MOVE,
      }));
    },
    [newCubicleKey, handleCreatePopup]
  );

  // 复制 cubicle
  const handleCopyCubicle = useCallback(
    ({ data, width, height }) => {
      setCubicleList((oldData) => {
        const newCubicle = {
          x: -10000,
          y: 0,
          width,
          height,
          id: newCubicleKey,
          data,
        };

        setNewCubicleKey((o) => o + 1);
        handleCreatePopup(false)();
        return [...oldData, newCubicle];
      });
      setToolStatus((o) => ({
        ...o,
        type: BS_TOOLBAR_STATUS.MOVE,
      }));
    },
    [newCubicleKey, handleCreatePopup]
  );

  // 删除 cubicle
  const handleDeleteCubicle = useCallback(
    (id) => () => {
      setCubicleList((o) =>
        _.filter(o, (item) => {
          if (item.id === id) {
            const cubicleBeds = _.filter(item.data.beds, (bedItem) => bedItem.data);

            setDragedBedList((dragedBedList) =>
              _.filter(
                dragedBedList,
                (dragedBedItem) =>
                  !_.some(
                    cubicleBeds,
                    (cubicleBedItem) =>
                      cubicleBedItem.data.bedStatusId === dragedBedItem.bedStatusId ||
                      _.some(
                        cubicleBedItem.data.linkeds,
                        (linkedItem) => linkedItem.bedStatusId === dragedBedItem.bedStatusId
                      )
                  )
              )
            );
            return false;
          } else {
            return true;
          }
        })
      );
    },
    []
  );

  // 修改 cubicle
  const handleEditCubicle = useCallback(
    ({ id, data }) => {
      setCubicleList((o) => {
        const resultList = _.map(o, (item) => {
          if (item.id === id) {
            return {
              ...item,
              data: { ...data },
            };
          } else {
            return item;
          }
        });

        return [...resultList];
      });
      setNewCubicleKey((o) => o + 1);
    },
    [newCubicleKey]
  );

  // 修改 nurse station
  const handleUpdateNurseStation = useCallback(
    ({ id, data }) => {
      setCubicleList((o) => {
        const resultList = _.map(o, (item) => {
          if (item.id === id) {
            return {
              ...item,
              data: { ...item.data, ...data },
            };
          } else {
            return item;
          }
        });

        return [...resultList];
      });
      setNewCubicleKey((o) => o + 1);
    },
    [newCubicleKey]
  );

  // 删除床位
  const handleDeleteBed = useCallback(({ cubicleId, index }) => {
    let deleteBed = {};
    setCubicleList((o) => {
      return _.map(o, (item) => {
        if (item.id === cubicleId) {
          return {
            ...item,
            data: {
              ...item.data,
              beds: _.map(item.data.beds, (bedItem, bedIndex) => {
                if (bedIndex === index) {
                  deleteBed = bedItem.data;
                  return {
                    ...bedItem,
                    data: null,
                  };
                } else {
                  return bedItem;
                }
              }),
            },
          };
        } else {
          return item;
        }
      });
    });

    setDragedBedList((o) => {
      return _.filter(o, (item) => Number(item.bedStatusId) !== Number(deleteBed.bedStatusId));
    });
  }, []);

  // 按照实际宽度和高度更新数据
  useUpdateEffect(() => {
    let tempCubicles = [];
    const foreignObjects = d3.selectAll('foreignObject');

    foreignObjects.each(function () {
      const foreignObject = d3.select(this);
      const childNode = foreignObject.select('div').node();
      const width = childNode.offsetWidth;
      const height = childNode.offsetHeight;

      foreignObject.attr('width', width).attr('height', height);
      tempCubicles.push({
        id: Number(foreignObject.attr('id')),
        x: Number(foreignObject.attr('x')),
        y: Number(foreignObject.attr('y')),
        width: Number(foreignObject.attr('width')),
        height: Number(foreignObject.attr('height')),
      });
    });

    setCubicleList((oldData) => {
      const result = _.map(oldData, (item) => {
        if (item.id === newCubicleKey - 1) {
          const tempCubicle = _.find(tempCubicles, (tempItem) => tempItem.id === item.id);
          const newCubicle = addNonOverlappingElement(oldData.slice(0, -1), { ...tempCubicle, x: 0, y: 0 });
          return {
            ...item,
            x: newCubicle.x,
            y: newCubicle.y,
            width: newCubicle.width,
            height: newCubicle.height,
          };
        }

        if (_.some(tempCubicles, (tempItem) => tempItem.id === item.id)) {
          const tempCubicle = _.find(tempCubicles, (tempItem) => tempItem.id === item.id);
          return {
            ...item,
            x: tempCubicle.x,
            y: tempCubicle.y,
            width: tempCubicle.width,
            height: tempCubicle.height,
          };
        } else {
          return item;
        }
      });

      return result;
    });
  }, [newCubicleKey]);

  return (
    <>
      <Box display="flex" flexDirection="column" width="100%" height="calc(100vh - 4.5rem - 4.375rem - 2.5rem)">
        {/* ward */}
        <Box display="flex" alignItems="center" gridGap="0.625rem" mb="0.25rem">
          <div className={classes.wardName}>{floorPlanData?.wardLocation}</div>
        </Box>
        {/* landing page */}
        <Box
          display="flex"
          flexDirection="column"
          border="solid 1px #e4e7eb"
          bgcolor="#fff"
          borderRadius="12px"
          padding="0.75rem"
          height="100%"
          width="calc(100% - 21.5rem)"
        >
          {/* Floor Plan */}
          <svg className={classes.floorRoot} ref={svgRef} width="100%" height="100%">
            <g className={classes.gRoot} ref={gRef}>
              {cubicleList.length === 0 && (
                <foreignObject className={classes.addCubicle} x="50%" y="50%" width="12.5rem" height="18rem">
                  <Box display="flex" flexDirection="column" alignItems="center" gridGap="1.5rem">
                    <Graphic className={classes.graphic} />
                    <div className={classes.addCubicleText}>Let&apos;s start to add one.</div>
                    <StyleButton
                      text="Add Cubicle"
                      Icon={AddIcon}
                      iconStyle={{ marginRight: '1rem' }}
                      width="100%"
                      type="fill"
                      onClick={handleCreatePopup(true)}
                    />
                  </Box>
                </foreignObject>
              )}
              {cubicleList.map((item) => (
                <foreignObject
                  key={item.id}
                  id={item.id}
                  x={item.x}
                  y={item.y}
                  width={item.width}
                  height={item.height}
                  style={{ overflow: 'visible' }}
                >
                  <Cubicle
                    {...item.data}
                    id={item.id}
                    handleDeleteCubicle={handleDeleteCubicle}
                    handleEditCubicle={handleEditCubicle}
                    handleCopyCubicle={handleCopyCubicle}
                    handleUpdateNurseStation={handleUpdateNurseStation}
                    tool={tool}
                    handleDeleteBed={handleDeleteBed}
                    handleUpdateLink={handleUpdateLink}
                    handleRemoveLinkedBed={handleRemoveLinkedBed}
                  />
                </foreignObject>
              ))}
            </g>
          </svg>
        </Box>
      </Box>
      {createPopOpen && (
        <CreateCubicle handleClose={handleCreatePopup(false)} handleCreateCubicle={handleCreateCubicle} />
      )}
    </>
  );
};

export default FloorPlan;
